import React, { useCallback, useEffect, useState } from 'react';
import { Button, Spin, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import ContentLayout from '../../layout/ContentLayout';
import { Container } from '../../components/content';
import { FloatingLabelInput } from '../../components/elements/floatingLabelInput/FloatingLabelInput';
import ToggleSwitch from '../../components/elements/toggleSwitch/ToggleSwitch';
import { fetchReferralConfig, updateReferralConfig } from '../../api/referral-config-api';
import styles from '../../styles/mobileInfoPage.module.scss';

const EMPTY_FORM = {
  welcomePoints: '',
  discountPercent: '',
  cashbackPercent: '',
  subscriptionCashbackPercent: '',
  externalProductCashbackPercent: '',
  mentorPackageCashbackPercent: '',
  productCashbackOverridesEnabled: true,
};

const FIELD_TOOLTIPS = {
  welcomePoints: 'Broj poena koji novi korisnik dobija odmah nakon registracije.',
  discountPercent:
    'Procenat popusta na ukupan iznos porudžbine za kupce koje je preporučio mentor (doktor). Primenjuje se samo ako porudžbina nema popust na količinu i ne sadrži eksterni proizvod.',
  cashbackPercent:
    'Procenat cashback-a (poena) za jednokratne porudžbine — primenjuje se na proizvode koji NEMAJU sopstveno cashback podešavanje (Webshop → Proizvodi → izmeni → "Cashback za ovaj proizvod").',
  subscriptionCashbackPercent:
    'Procenat cashback-a (poena) za pretplate (aktivacija, obnova) — primenjuje se na proizvode koji NEMAJU sopstveno cashback podešavanje.',
  externalProductCashbackPercent:
    'Procenat cashback-a koji se primenjuje samo na vrednost eksternih proizvoda u porudžbini. Ostatak porudžbine (ako ima drugih proizvoda) koristi opšti procenat cashback-a iznad.',
  mentorPackageCashbackPercent:
    'Procenat cashback-a za porudžbine Mentor Package proizvoda, koje inače ne donose cashback.',
};

const FieldWithTooltip = ({ tooltip, children }) => (
  <div style={{ position: 'relative' }}>
    {children}
    <Tooltip title={tooltip} overlayStyle={{ maxWidth: 360 }}>
      <InfoCircleOutlined
        style={{ position: 'absolute', top: 18, right: 12, color: '#6D758F', cursor: 'help' }}
        tabIndex={0}
      />
    </Tooltip>
  </div>
);

// Mirrors UpdateReferralConfigLoyaltyDto on the backend. Note welcomePoints/discountPercent/
// cashbackPercent/subscriptionCashbackPercent have no upper bound there (only Min(0)) — don't
// add a client-side <=100 cap those fields don't actually enforce.
const validate = (form) => {
  const nonNegativeFields = [
    { key: 'welcomePoints', label: 'Poeni za registraciju' },
    { key: 'discountPercent', label: 'Popust (%)' },
    { key: 'cashbackPercent', label: 'Cashback — jednokratne porudžbine (%)' },
    { key: 'subscriptionCashbackPercent', label: 'Cashback — pretplate (%)' },
  ];
  for (const { key, label } of nonNegativeFields) {
    const value = Number(form[key]);
    if (form[key] === '' || Number.isNaN(value) || value < 0) {
      toast.error(`"${label}" mora biti broj veći ili jednak 0`);
      return false;
    }
  }

  const percentFields = [
    { key: 'externalProductCashbackPercent', label: 'Cashback — eksterni proizvodi (%)' },
    { key: 'mentorPackageCashbackPercent', label: 'Cashback — Mentor Package (%)' },
  ];
  for (const { key, label } of percentFields) {
    const value = Number(form[key]);
    if (form[key] === '' || Number.isNaN(value) || value < 0 || value > 100) {
      toast.error(`"${label}" mora biti broj između 0 i 100`);
      return false;
    }
  }

  return true;
};

const buildPayload = (form) => ({
  welcomePoints: Number(form.welcomePoints),
  discountPercent: Number(form.discountPercent),
  cashbackPercent: Number(form.cashbackPercent),
  subscriptionCashbackPercent: Number(form.subscriptionCashbackPercent),
  externalProductCashbackPercent: Number(form.externalProductCashbackPercent),
  mentorPackageCashbackPercent: Number(form.mentorPackageCashbackPercent),
  productCashbackOverridesEnabled: form.productCashbackOverridesEnabled,
});

export default function LoyaltyProgramSettings() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    const { data, success, message } = await fetchReferralConfig();
    if (success && data) {
      setForm({ ...EMPTY_FORM, ...data.loyalty });
    } else {
      toast.error(message || 'Greška pri učitavanju podešavanja lojalnosti');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!validate(form)) return;
    setSaving(true);
    const { success, message } = await updateReferralConfig({ loyalty: buildPayload(form) });
    if (success) {
      toast.success('Podešavanja lojalnosti su sačuvana');
    } else {
      toast.error(message || 'Greška pri čuvanju podešavanja lojalnosti');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <Container>
        <ContentLayout>
          <div className={styles.loadingContainer}>
            <Spin size="large" />
          </div>
        </ContentLayout>
      </Container>
    );
  }

  return (
    <Container>
      <ContentLayout pt={true}>
        <h2 className={styles.pageTitle} style={{ marginBottom: '32px' }}>
          Podešavanja lojalnosti
        </h2>

        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Poeni i cashback</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '32px' }}>
            <FieldWithTooltip tooltip={FIELD_TOOLTIPS.welcomePoints}>
              <FloatingLabelInput
                label="Poeni za registraciju"
                type="number"
                value={form.welcomePoints}
                onChange={(e) => setField('welcomePoints', e.target.value)}
              />
            </FieldWithTooltip>
            <FieldWithTooltip tooltip={FIELD_TOOLTIPS.discountPercent}>
              <FloatingLabelInput
                label="Popust (%)"
                type="number"
                value={form.discountPercent}
                onChange={(e) => setField('discountPercent', e.target.value)}
              />
            </FieldWithTooltip>
            <FieldWithTooltip tooltip={FIELD_TOOLTIPS.cashbackPercent}>
              <FloatingLabelInput
                label="Cashback — jednokratne porudžbine (%)"
                type="number"
                value={form.cashbackPercent}
                onChange={(e) => setField('cashbackPercent', e.target.value)}
              />
            </FieldWithTooltip>
            <FieldWithTooltip tooltip={FIELD_TOOLTIPS.subscriptionCashbackPercent}>
              <FloatingLabelInput
                label="Cashback — pretplate (%)"
                type="number"
                value={form.subscriptionCashbackPercent}
                onChange={(e) => setField('subscriptionCashbackPercent', e.target.value)}
              />
            </FieldWithTooltip>
            <FieldWithTooltip tooltip={FIELD_TOOLTIPS.externalProductCashbackPercent}>
              <FloatingLabelInput
                label="Cashback — eksterni proizvodi (%)"
                type="number"
                value={form.externalProductCashbackPercent}
                onChange={(e) => setField('externalProductCashbackPercent', e.target.value)}
              />
            </FieldWithTooltip>
            <FieldWithTooltip tooltip={FIELD_TOOLTIPS.mentorPackageCashbackPercent}>
              <FloatingLabelInput
                label="Cashback — Mentor Package (%)"
                type="number"
                value={form.mentorPackageCashbackPercent}
                onChange={(e) => setField('mentorPackageCashbackPercent', e.target.value)}
              />
            </FieldWithTooltip>
          </div>
        </section>

        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Poseban cashback po proizvodu</h3>
          <ToggleSwitch
            checked={form.productCashbackOverridesEnabled}
            onChange={(e) => setField('productCashbackOverridesEnabled', e.target.checked)}
            label="Dozvoli posebna cashback podešavanja po proizvodu"
            tooltip="Kada je isključeno, SVA posebna cashback podešavanja po proizvodu (Webshop → Proizvodi → izmeni) se privremeno ignorišu — svi proizvodi koriste opšte procente iznad, bez obzira šta je podešeno na pojedinačnom proizvodu."
          />
        </section>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
          <Button className="btn-primary" type="primary" onClick={handleSave} loading={saving}>
            Sačuvaj izmene
          </Button>
        </div>
      </ContentLayout>
    </Container>
  );
}
